import React, {useState} from "react"
import {
	Alert,
	Box,
	Button,
	Checkbox,
	Chip,
	FormControlLabel,
	IconButton,
	Link as MuiLink,
	Step,
	StepLabel,
	Stepper,
	TableCell,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import {Link} from "react-router-dom"
import styled from "styled-components"

import {notificationOfAssignmentTitles} from "./AccountStatusIcon"
import type {NotificationOfAssignmentStatus} from "./AccountStatusIcon"

import {api} from "@indebted/api"
import type {AccountStatus, LetterNOA} from "@indebted/api"
import {useAgent} from "@indebted/hooks/Agent"
import {useNotification} from "@indebted/components/Notification"
import {colors} from "@indebted/theme"

const REQUEST_REASON = "Customer refused to provide an email"

// What the row and the panel's first step say before any letter is requested,
// per notice status. The server only sends a letter notice for these four, but
// the lookup is guarded so an unexpected status still gets a truthful chip.
const pendingCopy: Record<NotificationOfAssignmentStatus, {chip: string; requiredStep: string}> = {
	NotificationOfAssignmentPending: {chip: "NOA pending", requiredStep: "Required"},
	NotificationOfAssignmentPendingDueToEmailScrub: {
		chip: "NOA pending · no email",
		requiredStep: "Required · no usable email",
	},
	NotificationOfAssignmentPermanentlyUndelivered: {
		chip: "NOA undelivered",
		requiredStep: "Required · email undelivered",
	},
	NotificationOfAssignmentTemporarilyUndelivered: {
		chip: "NOA undelivered",
		requiredStep: "Required · email undelivered",
	},
}

function isNotificationOfAssignmentStatus(status: AccountStatus): status is NotificationOfAssignmentStatus {
	return status in pendingCopy
}

type ChipProps = {
	label: string
	color: "warning" | "info" | "success"
	tooltip: string
}

// Before any request the chip says what the account status says, shortened for
// the row, so an account whose customer still has an email reads "NOA pending"
// rather than claiming there is none.
function chipFor(status: AccountStatus, letterNOA: LetterNOA): ChipProps {
	if (letterNOA.State === "Sent") {
		return {
			label: `NOA letter sent ${letterNOA.SentAt}`,
			color: "success",
			tooltip: `Letter NOA sent on ${letterNOA.SentAt}, requested by ${letterNOA.RequestedBy}.`,
		}
	}
	if (letterNOA.State === "Requested") {
		return {
			label: "NOA letter requested",
			color: "info",
			tooltip: `Letter NOA requested by ${letterNOA.RequestedBy} on ${letterNOA.RequestedAt}. Waiting to be sent.`,
		}
	}
	if (!isNotificationOfAssignmentStatus(status)) {
		return {label: "NOA pending", color: "warning", tooltip: "Notification of assignment pending"}
	}
	return {label: pendingCopy[status].chip, color: "warning", tooltip: notificationOfAssignmentTitles[status]}
}

// A requested or sent letter means there was no usable email, whatever the
// status reads now; before that the first step names the status's own reason.
function requiredStepFor(status: AccountStatus, letterNOA: LetterNOA): string {
	if (letterNOA.State || !isNotificationOfAssignmentStatus(status)) {
		return "Required · no usable email"
	}
	return pendingCopy[status].requiredStep
}

function LetterNOAChip({
	status,
	letterNOA,
	open,
	onToggle,
}: {
	status: AccountStatus
	letterNOA: LetterNOA
	open: boolean
	onToggle: () => void
}) {
	const chip = chipFor(status, letterNOA)
	return (
		<>
			<Tooltip title={chip.tooltip} placement="right" describeChild>
				<StatusChip size="small" variant="outlined" label={chip.label} color={chip.color} onClick={onToggle} />
			</Tooltip>
			<IconButton
				size="small"
				onClick={onToggle}
				aria-label={open ? "Hide notice of assignment" : "Show notice of assignment"}
			>
				{open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
			</IconButton>
		</>
	)
}

function LetterNOAPanel({
	accountID,
	status,
	letterNOA,
	customerID,
	address,
	reload,
}: {
	accountID: string
	status: AccountStatus
	letterNOA: LetterNOA
	customerID: string
	address: string
	reload: (delayMs?: number) => void
}) {
	const [agentEmail] = useAgent()
	const {notification} = useNotification()
	const [refused, setRefused] = useState(false)
	const [sending, setSending] = useState(false)

	const activeStep = letterNOA.State === "Sent" ? 3 : letterNOA.State === "Requested" ? 1 : 0

	const handleSend = () => {
		setSending(true)
		api.account
			.requestLetterNotificationOfAssignment(accountID, {AgentEmail: agentEmail})
			.then(() => {
				setSending(false)
				setRefused(false)
				notification.success(
					"Letter Notification of Assignment requested. It goes out at the next compliant send time.",
				)
				reload(1000)
			})
			.catch((error: {Message: string}) => {
				setSending(false)
				notification.error(error.Message)
			})
	}

	return (
		<TableRow>
			<PanelCell colSpan={3}>
				<Box px={2} py={2} display="flex" flexDirection="column" gap={2}>
					<Typography variant="subtitle2" fontWeight={700}>
						Notice of Assignment
					</Typography>
					<Stepper activeStep={activeStep} sx={{maxWidth: 560}}>
						<Step>
							<StepLabel>{requiredStepFor(status, letterNOA)}</StepLabel>
						</Step>
						<Step>
							<StepLabel>
								{letterNOA.RequestedBy
									? `Letter requested by ${letterNOA.RequestedBy}`
									: "Letter requested"}
							</StepLabel>
						</Step>
						<Step>
							<StepLabel>{letterNOA.SentAt ? `Sent on ${letterNOA.SentAt}` : "Sent"}</StepLabel>
						</Step>
					</Stepper>

					{letterNOA.State === "Requested" && (
						<Alert severity="info" sx={{maxWidth: 560}}>
							Requested on {letterNOA.RequestedAt}. The letter goes out at the next compliant send time.
						</Alert>
					)}
					{letterNOA.State === "Sent" && (
						<Alert severity="success" sx={{maxWidth: 560}}>
							Letter posted on {letterNOA.SentAt}. Delivery is not tracked by the provider.
						</Alert>
					)}

					{(letterNOA.Allowed || letterNOA.State) && (
						<Typography variant="body2">
							<b>Posting to:</b> {address}
						</Typography>
					)}
					{letterNOA.Allowed && (
						<Box display="flex" flexDirection="column" gap={1} maxWidth={560}>
							<MuiLink component={Link} to={`/customers/${customerID}`} variant="body2">
								Edit address on the Overview tab
							</MuiLink>
							<FormControlLabel
								control={<Checkbox size="small" checked={refused} onChange={(_, v) => setRefused(v)} />}
								label={<Typography variant="body2">{REQUEST_REASON}</Typography>}
							/>
							<Box>
								<Button
									size="small"
									variant="contained"
									disabled={!refused || sending}
									onClick={handleSend}
								>
									Send letter NOA
								</Button>
							</Box>
						</Box>
					)}
					{!letterNOA.Allowed && !letterNOA.State && (
						<Typography variant="body2" color="textSecondary">
							Letter NOA not available: {letterNOA.IneligibilityMessage}
						</Typography>
					)}
				</Box>
			</PanelCell>
		</TableRow>
	)
}

const StatusChip = styled(Chip)`
	margin-right: 0.5rem;
	vertical-align: middle;
`

const PanelCell = styled(TableCell)`
	background-color: ${colors.lightGrey};
`

export {LetterNOAChip, LetterNOAPanel}
